import React from "react";
import { overlayBase, useParticles } from "./_shared";

interface Props {
  /** Number of leaves, default 10 */
  count?: number;
  /** Leaf colour, default autumn brown */
  color?: string;
  /** Base fall duration seconds, default 6 */
  dur?: number;
}

/**
 * Leaves — falling, rotating, swaying leaf-shaped elements.
 */
const Leaves: React.FC<Props> = ({
  count = 10,
  color = "#c98a3e",
  dur = 6,
}) => {
  const parts = useParticles(
    count,
    (i, r) => ({
      id: i,
      left: r(0) * 100,
      size: 8 + r(1) * 8,
      d: dur * (0.7 + r(2) * 0.7),
      delay: r(3) * dur,
      sway: 30 + r(4) * 40,
      spin: 360 + r(5) * 360,
    }),
    [count, dur]
  );

  return (
    <div style={overlayBase}>
      <style>{`
        @keyframes oot-leaf-fall {
          0%   { transform: translateY(-10px); opacity: 0; }
          10%  { opacity: 0.9; }
          100% { transform: translateY(105vh); opacity: 0.8; }
        }
        @keyframes oot-leaf-sway {
          0%, 100% { margin-left: calc(var(--sway) * -1); }
          50%       { margin-left: var(--sway); }
        }
        @keyframes oot-leaf-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(var(--spin)); }
        }
      `}</style>
      {parts.map((p) => (
        <div
          key={p.id}
          style={{
            position: "absolute",
            left: `${p.left}%`,
            top: 0,
            animation: `oot-leaf-fall ${p.d}s linear ${p.delay}s infinite`,
          }}
        >
          <div
            style={{
              animation: `oot-leaf-sway ${p.d / 2.5}s ease-in-out infinite`,
              "--sway": `${p.sway}px`,
            } as React.CSSProperties}
          >
            <div
              style={{
                width: p.size,
                height: p.size,
                background: color,
                borderRadius: "0% 70% 0% 70%",
                animation: `oot-leaf-spin ${p.d}s linear infinite`,
                "--spin": `${p.spin}deg`,
              } as React.CSSProperties}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default Leaves;
